import { useState, type ReactNode } from 'react'

import { CopyIcon } from '@/components/icons'
import type { InventoryVariant, PieceComposition, WarehouseProductTypeCode } from '@/lib/api/schemas/inventory.schema'
import { insertStonesText } from '@/lib/warehouse/composition'
import {
  matchBucketLabels,
  matchReadUnavailableReasonLabels,
  matchReasonLabels,
  matchRuleLabels,
  materialLabel,
  productTypeLabel,
  labelFor,
  unavailableReasonText,
} from '@/lib/warehouse/labels'
import { productUinOf } from '@/lib/warehouse/projection'
import { Fact, SectionCard, ValueOrReason } from '@/components/warehouse/primitives'

type FactValue = string | number | null
/** `recorded` renders only where a value exists: no row and no reason otherwise. */
type CharacteristicVisibility = 'always' | 'finding' | 'structural' | 'recorded'

export type StructuralFact = 'model' | 'shape' | 'brand' | 'material' | 'fineness' | 'productType' | 'weight' | 'maker'

type AbsenceDecision = {
  absent: number
  population: number
}

/**
 * The structural/finding decision is measured, not inferred. On 2026-09-13,
 * the 5,813-piece Warehouse population measured model 5,169 absent (89%),
 * shape 5,813 (100%), brand 5,767 (99%), material 56 (1%), fineness 56 (1%),
 * product type 0 and weight 0. Maker was re-measured on forge_staging_test at
 * 2026-09-13 12:56 UTC, once the piece carries its own maker: 118 absent (2%).
 * The 85% threshold is applied below: structural absences move to a title
 * attribute; findings stay on screen with their reason.
 *
 * Size uses the same threshold within each product type: pendants 100%,
 * brooches 100%, piercings 100%, earrings 99% are structural; rings 0%,
 * wedding rings 0%, chains 2%, bracelets 17%, necklaces 15%, and unspecified
 * pieces 56% are findings.
 */
const STRUCTURAL_ABSENCE_THRESHOLD = 0.85
const ABSENCE_DECISIONS: Record<StructuralFact, AbsenceDecision> = {
  model: { absent: 5_169, population: 5_813 },
  shape: { absent: 5_813, population: 5_813 },
  brand: { absent: 5_767, population: 5_813 },
  material: { absent: 56, population: 5_813 },
  fineness: { absent: 56, population: 5_813 },
  productType: { absent: 0, population: 5_813 },
  weight: { absent: 0, population: 5_813 },
  maker: { absent: 118, population: 5_813 },
}

const STRUCTURAL_FACTS = new Set<StructuralFact>(['model', 'shape', 'brand'])

const SIZE_ABSENCE_SHARES: Record<WarehouseProductTypeCode | 'unspecified', number> = {
  ring: 0,
  wedding_ring: 0,
  chain: 0.02,
  bracelet: 0.17,
  necklace: 0.15,
  earrings: 0.99,
  pendant: 1,
  brooch: 1,
  piercing: 1,
  // Cufflinks have no size by type; this is not an estimated census share.
  cufflinks: 1,
  unspecified: 0.56,
}

const DEFAULT_REASONS: Record<StructuralFact, string> = {
  model: 'model.grouping_not_reviewed',
  shape: 'shape.not_recorded',
  brand: 'brand.not_recorded',
  material: 'material.not_recorded',
  fineness: 'fineness.not_recorded',
  productType: 'product_type.not_recorded',
  weight: 'unit.weight_not_recorded',
  maker: 'manufacturer.not_recorded',
}

export function isStructuralPieceAbsence(fact: StructuralFact, value: FactValue): boolean {
  if (value !== null && value !== '') return false
  if (!STRUCTURAL_FACTS.has(fact)) return false
  const decision = ABSENCE_DECISIONS[fact]
  return decision.absent / decision.population >= STRUCTURAL_ABSENCE_THRESHOLD
}

function sizeAbsenceShare(productType: string | null): number {
  const known = Object.entries(SIZE_ABSENCE_SHARES).find(([code]) => code === productType)?.[1]
  return known ?? SIZE_ABSENCE_SHARES.unspecified
}

export function isStructuralPieceSizeAbsence(productType: string | null, size: string | null): boolean {
  if (size !== null && size !== '') return false
  return sizeAbsenceShare(productType) >= STRUCTURAL_ABSENCE_THRESHOLD
}

function reasonFor(fact: StructuralFact, reason: string | null): string {
  return reason ?? DEFAULT_REASONS[fact]
}

export interface PieceCharacteristicFact {
  label: string
  value: FactValue
  reason: string
  visibility: CharacteristicVisibility
}

function compositionFacts(composition: PieceComposition | null): PieceCharacteristicFact[] {
  if (composition === null) return []
  return [
    { label: 'Вес металла, г', value: composition.metalWeightGrams, reason: composition.metalWeightUnavailableReason ?? 'metal_weight.not_extracted', visibility: 'finding' },
    { label: 'Вставки', value: composition.insertStones === null ? null : insertStonesText(composition.insertStones), reason: composition.insertStonesUnavailableReason ?? 'insert_stones.not_extracted', visibility: 'finding' },
    { label: 'Вес вставок, г', value: composition.insertWeightGrams, reason: composition.insertWeightUnavailableReason ?? 'insert_weight.not_extracted', visibility: 'recorded' },
    { label: 'Вес вставок, кт', value: composition.insertWeightCarats, reason: composition.insertWeightCaratsUnavailableReason ?? 'insert_carats.not_extracted', visibility: 'recorded' },
  ]
}

export function pieceCharacteristicsOf(variant: InventoryVariant): readonly PieceCharacteristicFact[] {
  const { attributes, identity } = variant
  const size = identity.normalizedSize
  const brand = identity.brand
  const shape = attributes.shape
  const productType = attributes.productType
  const manufacturer = identity.manufacturer
  return [
    { label: 'Артикул', value: variant.article ?? null, reason: 'identity.article_absent', visibility: 'always' },
    { label: 'Размер', value: size, reason: 'identity.size_absent', visibility: isStructuralPieceSizeAbsence(productType, size) ? 'structural' : 'finding' },
    { label: 'Маркировка', value: productUinOf(variant), reason: variant.uinUnavailableReason ?? 'unit.marking_not_recorded_in_source', visibility: 'always' },
    { label: 'Вес, г', value: attributes.netWeightGrams, reason: reasonFor('weight', attributes.netWeightUnavailableReason), visibility: 'always' },
    ...compositionFacts(variant.composition),
    { label: 'Материал', value: attributes.material === null ? null : materialLabel(attributes.material), reason: reasonFor('material', attributes.materialUnavailableReason), visibility: isStructuralPieceAbsence('material', attributes.material) ? 'structural' : 'finding' },
    { label: 'Проба', value: attributes.fineness, reason: reasonFor('fineness', attributes.finenessUnavailableReason), visibility: isStructuralPieceAbsence('fineness', attributes.fineness) ? 'structural' : 'finding' },
    { label: 'Тип', value: productType === null ? null : productTypeLabel(productType), reason: reasonFor('productType', attributes.productTypeUnavailableReason), visibility: 'always' },
    { label: 'Форма', value: shape, reason: reasonFor('shape', attributes.shapeUnavailableReason), visibility: isStructuralPieceAbsence('shape', shape) ? 'structural' : 'always' },
    { label: 'Производитель', value: manufacturer, reason: reasonFor('maker', identity.manufacturerUnavailableReason), visibility: isStructuralPieceAbsence('maker', manufacturer) ? 'structural' : 'finding' },
    { label: 'Бренд', value: brand, reason: reasonFor('brand', identity.brandUnavailableReason), visibility: isStructuralPieceAbsence('brand', brand) ? 'structural' : 'always' },
  ]
}

function isPresent(value: FactValue): value is string | number {
  return value !== null && value !== ''
}

/** Pure copy projection: no unavailable reasons, technical identity, state or price. */
export function buildPieceCharacteristicsCopyText(variant: InventoryVariant): string {
  const lines = pieceCharacteristicsOf(variant)
    .filter((fact) => isPresent(fact.value))
    .map((fact) => `${fact.label}: ${fact.value}`)
  return [variant.title ?? '', ...lines].join('\n')
}

function structuralReasonTitle(facts: readonly PieceCharacteristicFact[]): string | undefined {
  const reasons = facts
    .filter((fact) => fact.visibility === 'structural' && !isPresent(fact.value))
    .map((fact) => `${fact.label}: ${unavailableReasonText(fact.reason)}`)
  return reasons.length > 0 ? reasons.join(' · ') : undefined
}

export function PieceCharacteristics({ variant }: { variant: InventoryVariant }) {
  const [copied, setCopied] = useState(false)
  const facts = pieceCharacteristicsOf(variant)
  const visibleFacts = facts.filter((fact) => (fact.visibility !== 'structural' && fact.visibility !== 'recorded') || isPresent(fact.value))
  const title = structuralReasonTitle(facts)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildPieceCharacteristicsCopyText(variant))
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div title={title} data-structural-absence={title}>
      <SectionCard
        title="Характеристики"
        action={(
          <button type="button" onClick={() => void copy()} aria-label={copied ? 'Скопировано' : 'Скопировать характеристики'} className="inline-flex items-center gap-1.5 rounded-md border border-rule px-2.5 py-1.5 text-[11px] text-ink-2 hover:bg-bone/60">
            <CopyIcon />
            {copied ? 'Скопировано' : 'Скопировать'}
          </button>
        )}
      >
        <dl className="grid grid-cols-2 gap-3">
          {visibleFacts.map((fact) => (
            <Fact key={fact.label} label={fact.label}>
              <ValueOrReason value={fact.value} reason={fact.reason} />
            </Fact>
          ))}
        </dl>
      </SectionCard>
    </div>
  )
}

export function TechnicalIdentityLog({ variant }: { variant: InventoryVariant }) {
  return (
    <dl className="grid grid-cols-2 gap-3">
      <Fact label="Идентификатор товара"><span className="font-mono text-[11px]">{variant.productId}</span></Fact>
      <Fact label="Правило сопоставления">
        <span className="text-ink-2">{variant.matchRule ? labelFor(matchRuleLabels, variant.matchRule) : 'Не публикуется'}</span>
        <span className="mt-0.5 block text-[10px] text-ink-muted">
          {variant.matchBucket ? matchBucketLabels[variant.matchBucket] : '—'}
          {variant.matchReason ? ` · ${labelFor(matchReasonLabels, variant.matchReason)}` : ''}
          {variant.matchUnavailableReason
            ? ` · ${labelFor(matchReadUnavailableReasonLabels, variant.matchUnavailableReason)}`
            : ''}
        </span>
      </Fact>
    </dl>
  )
}

export function LogsDisclosure({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <details id={id} className="rounded-lg border border-rule bg-paper">
      <summary className="cursor-pointer px-3 py-2 text-[11px] font-semibold text-ink-2">Логи</summary>
      <div className="space-y-3 border-t border-rule px-4 py-3">{children}</div>
    </details>
  )
}
